import { BadgeType } from 'components/Badge'
import { Project, ProjectDashboardStatus } from 'generated/types'
import { getFilterUrl } from 'helpers/filters'
import { boundsFromMapboxBbox } from 'helpers/mapbox'

type GeoLocation = {
  bbox?: [number, number, number, number] // [west, south, east, north]
  center?: [number, number]
  // eslint-disable-next-line camelcase
  mapbox_id?: string
  name?: string
  label?: string
}

type ProjectWithGeoLocation = Pick<Project, 'id' | 'budgetMax' | 'guestCount' | 'browsePath'> & {
  city?: {
    id: string
    name?: string
  } | null
  nexusProject?: {
    // eslint-disable-next-line camelcase
    geo_location?: GeoLocation
  } | null
  eventCategory?: {
    // eslint-disable-next-line camelcase
    defaultLocationTypeIds?: (string | null)[] | null
  } | null
}

export const projectBrowsePathWithFilters = (
  project: ProjectWithGeoLocation,
) => {
  const maxPrice = (project.budgetMax && project.guestCount)
    ? Math.trunc(parseInt(project.budgetMax) / project.guestCount)
    : undefined

  // eslint-disable-next-line camelcase
  const hasCity = !!project.city
  const geoLocation = project.nexusProject?.geo_location
  const hasBbox = geoLocation?.bbox && geoLocation.bbox.length === 4

  interface FilterParams {
    guestCount?: number | null
    maxPrice?: number
    projectId: string
    bounds?: {
      north: number
      east: number
      south: number
      west: number
    }
    mapboxId?: string
    locationTypes?: string[]
  }

  const filter: FilterParams = {
    guestCount: project.guestCount,
    maxPrice,
    projectId: project.id,
  }

  // Add bbox bounds if available from nexus geo_location
  if (!hasCity && hasBbox && geoLocation?.bbox) {
    filter.bounds = boundsFromMapboxBbox(geoLocation.bbox)

    // Include mapbox_id if available
    // eslint-disable-next-line camelcase
    if (geoLocation.mapbox_id) {
      // eslint-disable-next-line camelcase
      filter.mapboxId = geoLocation.mapbox_id
    }
  }

  // eslint-disable-next-line camelcase
  const defaultLocationTypeIds = project.eventCategory?.defaultLocationTypeIds

  if (defaultLocationTypeIds?.length) {
    filter.locationTypes = defaultLocationTypeIds.filter((id): id is string => id !== null)
  }

  return getFilterUrl({
    pathname: project.browsePath || undefined,
    filter,
    parameters: (!hasCity && hasBbox) ? { enableMap: '1' } : {},
  })
}

export const projectDashboardStatusToBadgeType: {[status in ProjectDashboardStatus]: BadgeType} = {
  cancelled: 'error-color',
  declined: 'error-color',
  declined_by_provider: 'error-color',
  proposed: 'blue-color',
  booked: 'success-color',
  price_indication: 'blue-color',
  offer_accepted: 'success-color',
  offer_binding_without_option: 'blue-color',
  offer_first_option: 'blue-color',
  offer_second_option: 'blue-color',
  interesting: 'attention-color',
  open: 'light-blue-color',
  waiting: 'attention-color',
}
